<script>
import axios from 'axios'
import formatBytes from '@utils/format-bytes'

const HOUR_MS = 3600000
const RANGE_HOURS = { '24h': 24, '7d': 168 }
const SITE_COLORS = ['#727cf5', '#0acf97', '#39afd1', '#ffbc00', '#fa5c7c', '#6c757d']
const RISK_VARIANTS = { 1: 'success', 2: 'success', 3: 'info', 4: 'warning', 5: 'danger' }

export default {
  props: {
    app: {
      type: String,
      default: ''
    },
    baseUrl: {
      type: String,
      default: 'http://localhost:5000'
    },
    basePath: {
      type: String,
      default: '/monitoring/acc'
    },
    site: {
      type: String,
      default: ''
    },
    // Range selected on the page; "1h" opens as 24 h because a single point
    // is not a trend.
    range: {
      type: String,
      default: '24h'
    }
  },
  data () {
    return {
      detail: {},
      detailRange: RANGE_HOURS[this.range] ? this.range : '24h',
      rangeOptions: [
        { value: '24h', text: '24 h' },
        { value: '7d', text: '7 d' }
      ],
      metric: 'sessions',
      metricOptions: [
        { value: 'sessions', text: 'Sessions' },
        { value: 'bytes', text: 'Bytes' }
      ],
      isBusy: false,
      loadError: ''
    }
  },
  computed: {
    // site -> hour start (ms) -> { sessions, bytes }. Collector runs land a
    // few minutes past the hour, and a failover hour can bring one doc from
    // each member, so points are bucketed and summed per hour.
    buckets () {
      const result = {}
      for (const point of this.detail.series || []) {
        const hour = Math.floor(Date.parse(point.ts) / HOUR_MS) * HOUR_MS
        const site = result[point.site] || (result[point.site] = {})
        const bucket = site[hour] || (site[hour] = { sessions: 0, bytes: 0 })
        bucket.sessions += point.sessions || 0
        bucket.bytes += point.bytes || 0
      }
      return result
    },
    siteNames () {
      return Object.keys(this.buckets).sort()
    },
    hourGrid () {
      const hours = []
      for (const site of this.siteNames) {
        for (const hour of Object.keys(this.buckets[site])) hours.push(+hour)
      }
      if (!hours.length) return []
      const grid = []
      for (let hour = Math.min(...hours); hour <= Math.max(...hours); hour += HOUR_MS) grid.push(hour)
      return grid
    },
    // Hours where the app was not in a site's top 25 stay null (a gap):
    // its real value is unknown, not zero.
    chartSeries () {
      return this.siteNames.map((site) => ({
        name: site,
        data: this.hourGrid.map((hour) => {
          const bucket = this.buckets[site][hour]
          return [hour, bucket ? bucket[this.metric] : null]
        })
      }))
    },
    chartOptions () {
      const format = this.metric === 'bytes' ? this.bytes : this.compact
      return {
        chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
        dataLabels: { enabled: false },
        stroke: { curve: 'straight', width: 2 },
        markers: { size: 3 },
        colors: SITE_COLORS,
        legend: { position: 'top', horizontalAlign: 'left' },
        // ApexCharts renders datetime axes in UTC unless told otherwise.
        xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
        yaxis: { min: 0, labels: { formatter: (value) => format(value) } },
        tooltip: {
          x: { format: 'dd MMM HH:mm' },
          y: { formatter: (value) => (value === null || value === undefined ? 'not in top 25' : format(value)) }
        }
      }
    },
    siteRows () {
      return this.siteNames.map((site) => {
        const hours = Object.values(this.buckets[site])
        return {
          site,
          hours: hours.length,
          sessions: hours.reduce((total, bucket) => total + bucket.sessions, 0),
          bytes: hours.reduce((total, bucket) => total + bucket.bytes, 0)
        }
      })
    },
    totals () {
      const hoursWithData = new Set()
      for (const site of this.siteNames) {
        for (const hour of Object.keys(this.buckets[site])) hoursWithData.add(hour)
      }
      let peak = null
      for (const hour of this.hourGrid) {
        const value = this.siteNames.reduce((total, site) =>
          total + ((this.buckets[site][hour] || {})[this.metric] || 0), 0)
        if (!peak || value > peak.value) peak = { hour, value }
      }
      return {
        sessions: this.siteRows.reduce((total, row) => total + row.sessions, 0),
        bytes: this.siteRows.reduce((total, row) => total + row.bytes, 0),
        hours: hoursWithData.size,
        expected: RANGE_HOURS[this.detailRange],
        peak
      }
    }
  },
  watch: {
    app () {
      this.loadDetail()
    },
    detailRange () {
      this.loadDetail()
    }
  },
  mounted () {
    this.loadDetail()
  },
  methods: {
    loadDetail () {
      if (!this.app) return
      this.isBusy = true
      this.loadError = ''
      const params = { range: this.detailRange }
      if (this.site) params.site = this.site
      axios
        .get(`${this.baseUrl}${this.basePath}/app/${encodeURIComponent(this.app)}`, { params })
        .then((response) => {
          this.detail = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
        })
        .catch((error) => {
          this.loadError = `Could not load application detail: ${error.message}`
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    bytes (value) {
      return formatBytes(value)
    },
    compact (value) {
      if (typeof value !== 'number') return '0'
      return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value)
    },
    riskVariant (risk) {
      return RISK_VARIANTS[risk] || 'secondary'
    },
    formatDate (value) {
      if (!value && value !== 0) return '—'
      return new Date(value).toLocaleString('en-CO', {
        timeZone: 'America/Bogota',
        hour12: false
      })
    }
  }
}
</script>

<template>
  <div>
    <b-alert
      :show="loadError.length > 0"
      variant="danger"
      dismissible
      @dismissed="loadError = ''"
    >
      {{ loadError }}
    </b-alert>

    <!-- Toolbar -->
    <div class="d-flex align-items-center flex-wrap mb-2">
      <span class="mr-2">
        Risk
        <b-badge :variant="riskVariant(detail.risk)">
          {{ detail.risk || '?' }}
        </b-badge>
      </span>
      <span class="text-muted font-size-12 mr-2">
        {{ site || 'All sites' }} · Bogota time
      </span>
      <b-form-radio-group
        v-model="detailRange"
        :options="rangeOptions"
        :disabled="isBusy"
        buttons
        button-variant="outline-primary"
        size="sm"
        class="ml-auto mr-2"
      />
      <b-form-radio-group
        v-model="metric"
        :options="metricOptions"
        buttons
        button-variant="outline-primary"
        size="sm"
      />
    </div>

    <div
      v-if="isBusy"
      class="text-center py-4"
    >
      <b-spinner class="align-middle" />
      <strong>&nbsp;&nbsp;Loading...</strong>
    </div>

    <div v-else-if="!siteNames.length">
      <div class="text-muted py-4 text-center">
        This application was not in any site's top 25 in this range.
      </div>
    </div>

    <div v-else>
      <!-- Facts -->
      <div class="row text-center mb-2">
        <div class="col-6 col-md-3">
          <div class="text-muted font-size-12">
            Sessions
          </div>
          <h4 class="my-0">
            {{ compact(totals.sessions) }}
          </h4>
        </div>
        <div class="col-6 col-md-3">
          <div class="text-muted font-size-12">
            Traffic
          </div>
          <h4 class="my-0">
            {{ bytes(totals.bytes) }}
          </h4>
        </div>
        <div class="col-6 col-md-3">
          <div class="text-muted font-size-12">
            Hours in top 25
          </div>
          <h4 class="my-0">
            {{ totals.hours }}<small class="text-muted font-size-13">/{{ totals.expected }}</small>
          </h4>
        </div>
        <div class="col-6 col-md-3">
          <div class="text-muted font-size-12">
            Peak hour ({{ metric }})
          </div>
          <h4 class="my-0 font-size-15 pt-1">
            {{ totals.peak ? formatDate(totals.peak.hour) : '—' }}
          </h4>
        </div>
      </div>

      <apexchart
        type="line"
        height="300"
        :series="chartSeries"
        :options="chartOptions"
      />
      <p class="text-muted font-size-12 mb-2">
        Gaps are hours where this application was not in the site's top 25 (or the
        collector missed the run): its traffic then is unknown, not zero.
      </p>

      <!-- Per site -->
      <table class="table table-sm font-size-13 mb-0">
        <thead>
          <tr class="text-muted">
            <th>Site</th>
            <th class="text-right">
              Hours in top 25
            </th>
            <th class="text-right">
              Sessions
            </th>
            <th class="text-right">
              Bytes
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in siteRows"
            :key="`site-${row.site}`"
          >
            <td class="font-weight-bold">
              {{ row.site }}
            </td>
            <td class="text-right">
              {{ row.hours }}/{{ totals.expected }}
            </td>
            <td class="text-right">
              {{ row.sessions.toLocaleString('en-US') }}
            </td>
            <td class="text-right">
              {{ bytes(row.bytes) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
